import {
  cn,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  SkeletonLoader,
} from '@merritrac/shared/components';
import { Clock, FileText } from 'lucide-react';

import type { ReactElement } from 'react';

interface ActivityEvent {
  id: string;
  type: string;
  message: string;
  timestamp: string;
  user: string;
}

interface ActivityFeedProps {
  activities: ActivityEvent[];
  loading?: boolean;
}

function formatTime(timestamp: string): string {
  if (!timestamp) {
    return '';
  }
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) {
    return timestamp;
  }
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function formatDate(timestamp: string): string {
  if (!timestamp) {
    return '';
  }
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) {
    return '';
  }
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return 'Today';
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function ActivityFeed({ activities, loading }: ActivityFeedProps): ReactElement {
  return (
    <GlassCard className="h-full flex flex-col">
      <GlassCardHeader>
        <h2 className="text-sm font-semibold text-text">Recent Activity</h2>
      </GlassCardHeader>
      <GlassCardContent className="flex-1 overflow-hidden relative py-2">
        {loading === true ? (
          <div className="space-y-4 py-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex gap-3">
                <SkeletonLoader variant="circle" width="32px" height="32px" />
                <div className="flex-1 space-y-1.5">
                  <SkeletonLoader variant="text" width="80%" />
                  <SkeletonLoader variant="text" width="50%" />
                </div>
              </div>
            ))}
          </div>
        ) : activities.length > 0 ? (
          <div className="flex flex-col divide-y divide-[var(--glass-border-subtle)] overflow-y-auto max-h-[320px] glass-scroll">
            {activities.map((activity) => (
              <div key={activity.id} className="flex items-start gap-3 py-3 px-1">
                <div
                  className={cn(
                    'flex items-center justify-center w-7 h-7 rounded-lg shrink-0 mt-0.5',
                    activity.type === 'clock-in'
                      ? 'bg-success-soft text-success'
                      : 'bg-primary-soft text-primary',
                  )}
                >
                  {activity.type === 'clock-in' ? <Clock size={13} /> : <FileText size={13} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-text truncate">{activity.message}</p>
                  <p className="text-[11px] text-text-tertiary mt-0.5">
                    {activity.user} &middot; {formatDate(activity.timestamp)}{' '}
                    {formatTime(activity.timestamp)}
                  </p>
                </div>
              </div>
            ))}
            {/* Fade-out at bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[var(--glass-bg)] to-transparent pointer-events-none" />
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-text-secondary">No recent activity</div>
        )}
      </GlassCardContent>
    </GlassCard>
  );
}
